// (c) TwistedMinds, Gerry Uytdenhouwen - 4 november 2026

import {buildCartLine} from './cartLines.js';
import {hasVariationsInOriginal, parseVariationIdentifiers, sumVariations} from './productVariations.js';

/**
 * Parses the variation list of a product (a JSON string in the catalogue)
 * @param product
 * @return {Array}
 */
function variationListOf(product) {
    if (Array.isArray(product.variation_list)) {
        return product.variation_list;
    }
    try {
        const parsed = JSON.parse(product.variation_list);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

/**
 * Turns the products of a previous order into cart lines, using the current
 * catalogue for name, image and prices. Pure: the cart itself is not touched.
 *
 * - product no longer in the catalogue: counted in `missing`
 * - chosen variation no longer offered: counted in `missing`
 * - price (or variation price) differs from the old order: product name in `priceChanged`
 *
 * @param {object[]} available  current catalogue products
 * @param {object[]} ordered  products of the previous order (ProductReorderResource)
 * @return {{lines: object[], missing: number, priceChanged: string[]}}
 */
export function buildReorderLines(available, ordered) {
    const lines = [];
    const priceChanged = [];
    let missing = 0;

    (ordered || []).forEach((productOrder) => {
        const product = (available || []).find(item => item.id === productOrder.id_product);
        if (!product) {
            missing++;
            return;
        }
        const variationList = variationListOf(product);
        const variationNames = parseVariationIdentifiers(productOrder.variation_name);
        if (!hasVariationsInOriginal(variationList, variationNames)) {
            missing++;
            return;
        }
        const productPrice = parseFloat(product.price) || 0;
        const variationPrice = sumVariations(variationList, variationNames);
        const orderPrice = parseFloat(productOrder.price) || 0;
        const orderVariationPrice = parseFloat(productOrder.variation_price) || 0;
        lines.push(Object.assign(
            buildCartLine(product, productOrder.qty_product, productOrder.variation_name, variationPrice),
            {'price': productPrice}
        ));
        if (productPrice !== orderPrice || variationPrice !== orderVariationPrice) {
            priceChanged.push(product.name);
        }
    });

    return {lines, missing, priceChanged};
}
